import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ShieldAlert, CheckCircle2, ArrowRight } from "lucide-react";
import { submitRestrictedAccessRequest } from "@/lib/api";
import { toast } from "sonner";

const CRIMSON = "#C8102E";

const COUNTRIES = [
    "United States", "Canada", "United Kingdom", "Germany", "France", "Netherlands",
    "Italy", "Spain", "Sweden", "Norway", "Denmark", "Finland", "Switzerland",
    "Austria", "Belgium", "Ireland", "Portugal", "Poland", "Czechia", "Australia",
    "New Zealand", "Mexico", "Brazil", "Argentina", "Chile", "Japan", "South Korea",
    "Singapore", "United Arab Emirates", "South Africa", "Other",
];

const HOW_FOUND_OPTIONS = [
    "Telegram", "Reddit", "Search engine", "Forum / community", "Word of mouth",
    "Existing customer", "Social media", "Other",
];

export default function RestrictedAccess() {
    const [form, setForm] = useState({
        email: "",
        username: "",
        country: "",
        how_found: "",
        research_experience: "",
        interests: "",
        research_only_acknowledged: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.research_only_acknowledged) {
            toast.error("You must confirm research-only use.");
            return;
        }
        if (form.research_experience.trim().length < 20) {
            toast.error("Please provide at least 20 characters about your research background.");
            return;
        }
        setSubmitting(true);
        try {
            await submitRestrictedAccessRequest(form);
            setDone(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Submission failed, please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = "w-full bg-ink-base border border-[#3a1418] focus:border-[#C8102E] text-ink px-4 py-3 text-sm outline-none transition-colors placeholder:text-ink-mute";
    const labelCls = "overline text-[10px] tracking-[0.2em] mb-1.5 block";

    if (done) {
        return (
            <div className="min-h-[80vh] bg-ink-base flex items-center justify-center px-6" data-testid="restricted-access-success">
                <div className="max-w-lg w-full border-t-4 border-[#C8102E] bg-ink-surface p-10 text-center">
                    <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: CRIMSON }} strokeWidth={1.5} />
                    <h1 className="font-display text-3xl font-bold text-ink mt-6">Request received.</h1>
                    <p className="text-sm text-ink-dim mt-4 leading-relaxed">
                        We will review and contact you shortly. If approved, credentials for the restricted research catalog will be delivered to your email within 24 to 48 hours.
                    </p>
                    <Link to="/" className="inline-flex items-center gap-2 mt-8 text-xs font-mono uppercase tracking-[0.18em] text-ink-dim hover:text-ink transition-colors" data-testid="restricted-success-home">
                        Return to catalog <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                    <p className="mt-10 text-[10px] font-mono text-[#C8102E]/80 leading-relaxed">
                        STRICTLY FOR LABORATORY AND IN-VITRO RESEARCH PURPOSES ONLY. NOT FOR HUMAN OR ANIMAL CONSUMPTION.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ink-base text-ink" data-testid="restricted-access-page">
            <div className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12 py-16">
                <div className="border-b pb-10 mb-10" style={{ borderColor: "#3a1418" }}>
                    <div className="flex items-center gap-2 mb-4">
                        <Lock className="w-4 h-4" style={{ color: CRIMSON }} strokeWidth={1.5} />
                        <p className="overline text-[11px] tracking-[0.22em]" style={{ color: CRIMSON }}>/ RESTRICTED RESEARCH CATALOG</p>
                    </div>
                    <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[0.92]">
                        Request<br /><span style={{ color: CRIMSON }}>access.</span>
                    </h1>
                    <p className="mt-6 text-ink-dim max-w-2xl leading-relaxed">
                        This section contains advanced research compounds (including anabolic steroids) intended strictly for laboratory and scientific use only.
                    </p>
                    <p className="mt-3 text-ink-dim max-w-2xl leading-relaxed">
                        To maintain the highest standards and compliance, access is granted on a case-by-case basis. Please submit the form below. Approved researchers will receive login credentials within 24 to 48 hours.
                    </p>
                </div>

                <form onSubmit={submit} className="space-y-6" data-testid="restricted-access-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <label className="block">
                            <span className={labelCls} style={{ color: CRIMSON }}>EMAIL ADDRESS *</span>
                            <input type="email" required value={form.email} onChange={set("email")}
                                className={inputCls} placeholder="lab@institution.org" data-testid="restricted-input-email" />
                        </label>
                        <label className="block">
                            <span className={labelCls} style={{ color: CRIMSON }}>USERNAME (DESIRED) *</span>
                            <input type="text" required minLength={3} maxLength={32} value={form.username}
                                onChange={(e) => setForm({ ...form, username: e.target.value.replace(/[^a-zA-Z0-9_-]/g, "") })}
                                className={inputCls} placeholder="researcher_42" data-testid="restricted-input-username" />
                        </label>

                        <label className="block">
                            <span className={labelCls} style={{ color: CRIMSON }}>COUNTRY *</span>
                            <select required value={form.country} onChange={set("country")}
                                className={inputCls} data-testid="restricted-input-country">
                                <option value="">Select country</option>
                                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </label>
                        <label className="block">
                            <span className={labelCls} style={{ color: CRIMSON }}>HOW DID YOU FIND US? *</span>
                            <select required value={form.how_found} onChange={set("how_found")}
                                className={inputCls} data-testid="restricted-input-how-found">
                                <option value="">Select source</option>
                                {HOW_FOUND_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </label>
                    </div>

                    <label className="block">
                        <span className={labelCls} style={{ color: CRIMSON }}>RESEARCH EXPERIENCE / BACKGROUND *</span>
                        <textarea required rows={5} minLength={20} maxLength={4000} value={form.research_experience}
                            onChange={set("research_experience")}
                            className={inputCls + " resize-y leading-relaxed"}
                            placeholder="Describe your laboratory affiliation, role, prior research with restricted compounds, certifications, or relevant academic background. Minimum 20 characters."
                            data-testid="restricted-input-experience" />
                        <span className="text-[10px] font-mono text-ink-mute mt-1 block">{form.research_experience.length} / 4000</span>
                    </label>

                    <label className="block">
                        <span className={labelCls} style={{ color: CRIMSON }}>WHAT COMPOUNDS ARE YOU PRIMARILY INTERESTED IN? *</span>
                        <textarea required rows={3} maxLength={2000} value={form.interests}
                            onChange={set("interests")}
                            className={inputCls + " resize-y leading-relaxed"}
                            placeholder="e.g. Testosterone esters for HPG-axis pharmacokinetic studies, Trenbolone for receptor-binding research."
                            data-testid="restricted-input-interests" />
                    </label>

                    <label className="flex items-start gap-3 p-4 bg-ink-surface border cursor-pointer hover:bg-ink-base/40 transition-colors"
                        style={{ borderColor: form.research_only_acknowledged ? CRIMSON : "#3a1418" }}>
                        <input type="checkbox" required checked={form.research_only_acknowledged}
                            onChange={(e) => setForm({ ...form, research_only_acknowledged: e.target.checked })}
                            className="mt-0.5 w-4 h-4 accent-[#C8102E]" data-testid="restricted-input-acknowledge" />
                        <span className="text-xs text-ink-dim leading-relaxed">
                            <strong className="text-ink">I confirm</strong> that I understand these compounds are for <span style={{ color: CRIMSON }}>research use only</span> and <span style={{ color: CRIMSON }}>NOT for human consumption</span>.
                        </span>
                    </label>

                    <button type="submit" disabled={submitting}
                        className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 font-bold tracking-wider text-sm uppercase transition-colors disabled:opacity-50"
                        style={{ background: CRIMSON, color: "#fff" }}
                        data-testid="restricted-submit-btn">
                        {submitting ? "SUBMITTING…" : "REQUEST ACCESS"} {!submitting && <ArrowRight className="w-4 h-4" />}
                    </button>

                    <div className="border-t pt-6 mt-8" style={{ borderColor: "#2a1418" }}>
                        <div className="flex items-start gap-3">
                            <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" style={{ color: CRIMSON }} strokeWidth={1.5} />
                            <p className="text-[11px] font-mono uppercase tracking-[0.15em] leading-relaxed" style={{ color: CRIMSON }}>
                                STRICTLY FOR LABORATORY AND IN-VITRO RESEARCH PURPOSES ONLY. NOT FOR HUMAN OR ANIMAL CONSUMPTION.
                            </p>
                        </div>
                    </div>
                </form>

                <div className="mt-12 text-xs text-ink-dim text-center">
                    Already a member? <Link to="/restricted/login" className="hover:underline" style={{ color: CRIMSON }} data-testid="restricted-access-login-link">Sign in →</Link>
                </div>
            </div>
        </div>
    );
}
